(() => {
if (window.__primeSearchInstalled) return;
window.__primeSearchInstalled = true;
/* Prime Welding search: one resolver for suggestions and full results. */
const PrimeSearchCore = (() => {
  const fold = value => String(value || '').toLowerCase().replace(/ß/g, 'ss').normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const compact = value => fold(value).replace(/[^a-z0-9]/g, '');
  const quoted = value => '"' + String(value).replace(/["\\]/g, '\\$&') + '"';
  function build(products) {
    const skus = new Map(), aliases = new Map();
    for (const product of products) {
      for (const variant of product.variants || []) {
        const key = compact(variant[0]);
        if (key) {
          if (!skus.has(key)) skus.set(key, []);
          skus.get(key).push({product, variant});
        }
      }
      // Only short technical codes, not arbitrary words or product equivalences.
      const tokens = product.title.match(/[\p{L}\d]+(?:[.,]\d+)*/gu) || [];
      for (let start = 0; start < tokens.length; start++) {
        for (let length = 1; length <= 3 && start + length <= tokens.length; length++) {
          const words = tokens.slice(start, start + length);
          const phrase = words.join(' '), key = compact(phrase);
          const code = words.every(w => /^[A-Z0-9]+(?:[.,]\d+)*$/.test(w) && w.length <= 6);
          if (!code || key.length < 3 || key.length > 12 || !/[A-Z]/.test(phrase)) continue;
          if (length > 1 && !words.some(w => w.length <= 2 || /\d/.test(w))) continue;
          if (!aliases.has(key)) aliases.set(key, new Set());
          aliases.get(key).add(phrase);
        }
      }
    }
    return {products, skus, aliases};
  }
  function resolve(index, input) {
    const raw = input.trim().replace(/\s+/g, ' '), key = compact(raw);
    // Preserve deliberately entered Shopify query syntax.
    if (!key || /[:"*()]/.test(raw) || /\b(?:AND|OR|NOT)\b/.test(raw)) return {query: raw, exact: []};
    const exact = index?.skus.get(key) || [];
    if (exact.length) {
      const values = [...new Set(exact.map(row => row.variant[0]))];
      if (values.length <= 20) return {query: values.map(v => 'variants.sku:' + quoted(v)).join(' OR '), exact};
    }
    const aliases = index?.aliases.get(key);
    if (aliases?.size && aliases.size <= 8) {
      return {query: [...aliases].map(v => 'title:' + quoted(v)).join(' OR '), exact: []};
    }
    return {query: raw, exact: []};
  }
  function selectVariant(product, input) {
    const key = compact(input);
    return (product.variants || []).find(v => v[0] && compact(v[0]) === key) || null;
  }
  return {compact, build, resolve, selectVariant};
})();
if (typeof module !== 'undefined') module.exports = PrimeSearchCore;

const PrimeSearch = (() => {
  const root = window.Shopify?.routes?.root || '/';
  const cacheKey = 'prime-search-v1:' + location.host + ':' + root + ':' + (window.Shopify?.country || '');
  let indexPromise;
  const de = document.documentElement.lang.startsWith('de');
  const labels = de ? {all:'Alle Ergebnisse anzeigen', empty:'Keine passenden Artikel gefunden.', sku:'Art.-Nr.', variant:'Variante', from:'ab', more:'Weitere passende und ähnliche Artikel', loading:'Suche läuft …'} : {all:'View all results', empty:'No matching products found.', sku:'SKU', variant:'Variant', from:'from', more:'More matching and related products', loading:'Searching …'};
  async function json(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {signal: controller.signal, credentials:'same-origin'});
      if (!response.ok) throw new Error('Search data unavailable');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function index() {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(cacheKey));
        if (saved && Date.now() - saved.time < 15 * 60 * 1000 && Array.isArray(saved.products)) return PrimeSearchCore.build(saved.products);
      } catch (_) {}
      const url = new URL(root + 'collections/all', location.origin);
      url.searchParams.set('view', 'prime-search-index');
      const first = await json(url);
      if (!Array.isArray(first.products) || !Number.isInteger(first.pages) || first.pages > 100) throw new Error('Invalid search index');
      const products = first.products;
      let nextPage = 2;
      await Promise.all([0,1,2].map(async () => {
        while (nextPage <= first.pages) {
          const page = nextPage++;
          const pageUrl = new URL(url); pageUrl.searchParams.set('page', page);
          const data = await json(pageUrl);
          if (!Array.isArray(data.products)) throw new Error('Incomplete search index');
          products.push(...data.products);
        }
      }));
      try { sessionStorage.setItem(cacheKey, JSON.stringify({time:Date.now(),products})); } catch (_) {}
      return PrimeSearchCore.build(products);
    })().catch(error => { indexPromise = null; throw error; });
    return indexPromise;
  }
  async function resolve(input, category='') {
    let data = null;
    try { data = await index(); } catch (_) { /* Native search remains available on failure. */ }
    const result = PrimeSearchCore.resolve(data, input);
    if (category) result.query = '(' + result.query + ') AND product_type:' + '"' + category.replace(/["\\]/g,'\\$&') + '"';
    return result;
  }
  function url(query, input, category='') {
    const target = new URL(root + 'search', location.origin);
    target.searchParams.set('q', query);
    target.searchParams.set('type','product');
    target.searchParams.set('options[prefix]','last');
    if (query !== input) target.searchParams.set('pwq',input);
    if (category) target.searchParams.set('pwtype',category);
    return target;
  }
  const element = (tag, text, cls) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (cls) node.className = cls;
    return node;
  };
  function metadata(product, input) {
    const exact = PrimeSearchCore.selectVariant(product,input);
    const only = product.variants?.length === 1 ? product.variants[0] : null;
    const chosen = exact || only;
    const lines = [];
    if (chosen?.[0]) lines.push(labels.sku + ': ' + chosen[0]);
    if (chosen?.[2] && chosen[2] !== 'Default Title') lines.push(labels.variant + ': ' + chosen[2]);
    if (!chosen && product.variants?.length) {
      const variants = product.variants.filter(v => v[0]).slice(0,3);
      if (variants.length) lines.push(labels.sku + ': ' + variants.map(v=>v[0]).join(' · ') + (product.variants.length > 3 ? ' …' : ''));
      const names = [...new Set(product.variants.map(v=>v[2]).filter(v=>v && v!=='Default Title'))];
      if (names.length) lines.push(labels.variant + ': ' + names.slice(0,2).join(' · ') + (names.length > 2 ? ' …' : ''));
    }
    return {exact, lines};
  }
  function render(target, products, input, resultUrl, limit) {
    target.replaceChildren();
    const grid = element('div',null,'prime-search-suggestions');
    for (const product of products.slice(0,limit)) {
      const meta = metadata(product,input);
      const href = new URL(product.url,location.origin);
      if (href.origin !== location.origin) continue;
      if (meta.exact) href.searchParams.set('variant',meta.exact[1]);
      const link = element('a',null,'prime-search-hit'); link.href = href;
      if (product.image) {
        const image = element('img'); image.src = product.image; image.alt = ''; image.width=72; image.height=72; image.loading='lazy'; link.append(image);
      }
      const content = element('span');
      content.append(element('strong',product.title));
      for (const line of meta.lines) content.append(element('small',line));
      const price = meta.exact?.[3] || product.price;
      if (price) content.append(element('span', (product.varies && !meta.exact ? labels.from+' ' : '') + price));
      link.append(content); grid.append(link);
    }
    target.append(grid);
    if (!products.length) target.append(element('p',labels.empty));
    const all = element('a',labels.all,'btn btn--primary'); all.href=resultUrl; target.append(all);
  }
  async function suggestions(input,category) {
    const resolved = await resolve(input,category);
    const resultUrl = url(resolved.query,input,category);
    const endpoint = new URL(resultUrl); endpoint.searchParams.set('view','prime-search-data');
    const data = await json(endpoint);
    if (!Array.isArray(data.products)) throw new Error('Invalid search response');
    return {products:data.products,url:resultUrl};
  }
  async function improveResults() {
    if (location.pathname.replace(/\/$/,'') !== (root+'search').replace(/\/$/,'')) return;
    const params = new URLSearchParams(location.search);
    const input = params.get('pwq') || params.get('q') || '';
    if (!input.trim()) return;
    const category = params.get('pwtype') || '';
    if (params.has('pwq')) document.title = document.title.replace(params.get('q'), input);
    if (!params.has('pwq')) {
      const result = await resolve(input,category);
      if (result.query !== params.get('q')) {
        const target = new URL(location.href);
        target.searchParams.set('q', result.query); target.searchParams.set('pwq',input); target.searchParams.set('type','product'); target.searchParams.delete('page');
        location.replace(target); return;
      }
    }
    const inputs = document.querySelectorAll('predictive-search input[name=q]');
    for (const field of inputs) if (field.value) field.value=input;
    for (const select of document.querySelectorAll('predictive-search #SearchProductTypes')) select.value=category;
    let catalog;
    try { catalog = await index(); } catch (_) { return; }
    const byHandle = new Map(catalog.products.map(p=>[p.handle,p]));
    const enhance = () => {
      const cards = document.querySelectorAll('#ProductGridContainer .f-column.card');
      for (const card of cards) {
        if (card.dataset.primeEnhanced) continue;
        const title = card.querySelector('.product-card__title');
        const anchor = title?.querySelector('a');
        if (!anchor) continue;
        const match = new URL(anchor.href).pathname.match(/\/products\/([^/]+)/);
        const product = match && byHandle.get(decodeURIComponent(match[1]));
        if (!product) continue;
        card.dataset.primeEnhanced='1';
        const meta = metadata(product,input);
        const info = element('div',null,'prime-search-meta');
        for (const line of meta.lines) info.append(element('small',line));
        title.after(info);
        if (meta.exact) {
          for (const link of card.querySelectorAll('a[href*="/products/"]')) {
            const target = new URL(link.href); target.searchParams.set('variant',meta.exact[1]); link.href=target;
          }
          // The native card can show a family minimum price. State this clearly,
          // and use the selected variant page, never quick-add another variant.
          for (const button of card.querySelectorAll('quick-add, .quick-add, product-form, button[name="add"]')) button.remove();
          if (product.variants.length > 1) info.append(element('small',de ? 'Preis der gewählten Variante auf der Artikelseite' : 'Selected variant price on product page'));
        }
      }
    };
    enhance();
    const container=document.querySelector('.template-search');
    if (container) new MutationObserver(enhance).observe(container,{childList:true,subtree:true});
    // Keep the human query across native filter/sort form submissions.
    for (const form of document.querySelectorAll('facet-filters-form form, facet-sort form')) {
      if (params.has('pwq') && !form.querySelector('[name=pwq]')) {const hidden=element('input');hidden.type='hidden';hidden.name='pwq';hidden.value=input;form.append(hidden);}
    }
  }
  if (!document.getElementById('prime-search-styles')) {
    const style=element('style'); style.id='prime-search-styles';
    style.textContent='.prime-search-suggestions{display:grid;gap:14px;margin-bottom:18px}.prime-search-hit{display:flex;gap:14px;align-items:center;padding:10px;border-bottom:1px solid #8883;text-decoration:none;color:inherit}.prime-search-hit img{object-fit:contain;flex:none;background:#fff;border-radius:6px}.prime-search-hit>span{display:grid;gap:4px;min-width:0}.prime-search-hit small,.prime-search-meta small{display:block;overflow-wrap:anywhere;font-size:12px;line-height:1.45}.prime-search-meta{margin:6px 0}.prime-search-hit:focus-visible{outline:2px solid currentColor;outline-offset:2px}';
    document.head.append(style);
  }
  document.readyState==='loading' ? document.addEventListener('DOMContentLoaded',()=>improveResults().catch(console.warn)) : improveResults().catch(console.warn);
  return {index, resolve, url, suggestions, render, labels};
})();

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestVersion = 0;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.querySelector('form')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = this.getQuery();
      if (!input) return;
      const category = this.searchProductTypes?.value || '';
      this.setLoadingState(true);
      try {
        const result = await PrimeSearch.resolve(input, category);
        location.assign(PrimeSearch.url(result.query, input, category));
      } finally { this.setLoadingState(false); }
    });

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    PrimeSearch.index().catch(() => {});
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    this.requestVersion++;
    this.setLoadingState(false);
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  async renderSection(url) {
    const version = ++this.requestVersion;
    const input = this.getQuery();
    const category = this.searchProductTypes?.value || '';
    if (!input) return;
    this.setLoadingState(true);
    try {
      const data = await PrimeSearch.suggestions(input, category);
      if (version !== this.requestVersion || input !== this.getQuery()) return;
      const target = this.querySelector('[id^="PredictiveSearchResults-"]');
      if (!target) return;
      PrimeSearch.render(target, data.products, input, data.url, Math.max(3, Math.min(8, Number(this.dataset.resultsLimit) || 5)));
      this.searchContent?.classList.remove('hidden');
      this.toggleSearchState(true);
      this.input.setAttribute('aria-expanded','true');
      this.setAttribute('results','');
    } catch (error) {
      if (version !== this.requestVersion) return;
      // Do not silently mix the predictive endpoint's different result set.
      const target = this.querySelector('[id^="PredictiveSearchResults-"]');
      if (target) {
        target.replaceChildren();
        const link = document.createElement('a');
        link.textContent=PrimeSearch.labels.all;
        link.href=PrimeSearch.url(input,input,category);
        target.append(link);
        this.searchContent?.classList.remove('hidden'); this.toggleSearchState(true);
      }
    } finally { if (version === this.requestVersion) this.setLoadingState(false); }
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.input.setAttribute('aria-expanded', String(isOpen));
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

})();
